import { useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_MAIN_BRANCH_NAME } from 'constants/Multidraft.constants';
import { useApi } from 'contexts/ApiContext';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { MAIN_BRANCH_ID } from '@polyai/stream/constants';
import Text from '@polyai/ui/components/atoms/Text';
import { Modal } from '@polyai/ui/components/molecules/Modal';

import FormProvider, { FormTextInput } from 'components/molecules/Form';
import useBranches, {
  getBranchesQueryKey,
} from 'hooks/agentStream/draft/useBranches';
import useCreateDraft from 'hooks/agentStream/draft/useCreateDraft';
import { getUniqueName } from 'hooks/useFlows/useFlows';
import { useToast } from 'hooks/useToast';

const CreateBranchModal = ({
  accountId,
  projectId,
  onBranchCreated,
  open,
  setOpen = () => {},
  onCancel = () => {},
}: {
  accountId: string;
  projectId: string;
  onBranchCreated: (branchId: string) => void;
  open: boolean;
  setOpen?: (open: boolean) => void;
  onCancel?: () => void;
}) => {
  const isCreatingRef = useRef(false);
  // Only fetch branches when the modal is open to avoid per-project requests
  const { data: branches } = useBranches(projectId, open);
  const toast = useToast();

  const Schema = useMemo(
    () =>
      z.object({
        name: z
          .string({
            required_error: 'Branch name is required',
          })
          .min(1, 'Branch name is required')
          .max(100, 'Branch name must be less than 100 characters')
          .refine(
            name =>
              name.toLowerCase() !== DEFAULT_MAIN_BRANCH_NAME.toLowerCase() &&
              name.toLowerCase() !== MAIN_BRANCH_ID.toLowerCase(),
            {
              message: `Branch name cannot be "${DEFAULT_MAIN_BRANCH_NAME}" or "${MAIN_BRANCH_ID}"`,
            },
          )
          .refine(
            name => !branches?.branches?.some(branch => branch.name === name),
            {
              message: 'Branch name must be unique',
            },
          ),
      }),
    [branches],
  );

  type SchemaProps = z.input<typeof Schema>;

  type FormProps = z.output<typeof Schema>;

  const methods = useForm<SchemaProps, unknown, FormProps>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: getUniqueName(
        branches?.branches?.map(branch => branch.name) ?? [],
        'Branch',
      ),
    },
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const queryClient = useQueryClient();
  const api = useApi();
  const { mutateAsync: createBranch, isLoading: isCreatingBranch } =
    useCreateDraft(undefined, projectId);

  const onSubmit: SubmitHandler<FormProps> = async data => {
    if (isCreatingRef.current) {
      return;
    }
    isCreatingRef.current = true;
    try {
      const response = await api.methods.getLatestSequence({
        accountId,
        projectId: projectId,
        branchId: MAIN_BRANCH_ID,
      });
      const { branchId } = await createBranch({
        lastKnownSequence: Number(response.lastKnownSequence),
        branchName: data.name,
      });
      onBranchCreated(branchId);
      await queryClient.resetQueries(getBranchesQueryKey(accountId, projectId));
    } catch {
      toast.error({
        title: 'Error creating branch',
        description:
          'There was an error creating the branch, please refresh the page and try again.',
      });
    } finally {
      isCreatingRef.current = false;
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <Modal.Content data-test-id="create-branch-modal">
        <Modal.Title>Create Branch</Modal.Title>
        <Text marginBottom="sm2">
          Branch is an isolated draft environment that lets you safely edit your
          agent without affecting its main version.
        </Text>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <FormTextInput<FormProps>
            controlKey="name"
            data-test-id="branch-name-input"
            label="Branch name"
            placeholder="Enter branch name"
          />
        </FormProvider>
        <Modal.Footer
          primary={{
            label: 'Create',
            variant: 'PRIMARY',
            onClick: () => void methods.handleSubmit(onSubmit)(),
            'data-test-id': 'create-branch-button',
            loading: isCreatingBranch,
            disabled: isCreatingBranch,
          }}
          secondary={{
            label: 'Cancel',
            onClick: () => {
              setOpen(false);
              onCancel();
            },
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

export default CreateBranchModal;
